import { cn } from '../../utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  to: string;
  icon: JSX.Element;
}

// ── SVG helper ────────────────────────────────────────────────────────────────

/** Shared stroked-icon renderer used across sidebar, drawer, and bottom nav. */
export function SvgIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg
      className={cn('h-5 w-5 flex-shrink-0', className)}
      fill="none" viewBox="0 0 24 24"
      stroke="currentColor" strokeWidth={1.75}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// ── Icon path constants (reused by multiple surfaces) ─────────────────────────

export const ICON_HOME =
  'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';
export const ICON_FILES =
  'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z';
export const ICON_AI =
  'M13 10V3L4 14h7v7l9-11h-7z';
export const ICON_HISTORY =
  'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z';

// ── Nav data ──────────────────────────────────────────────────────────────────

export const HOME_ITEM: NavItem = {
  label: 'Home', to: '/dashboard', icon: <SvgIcon path={ICON_HOME} />,
};

export const FILES_ITEM: NavItem = {
  label: 'File Manager', to: '/files', icon: <SvgIcon path={ICON_FILES} />,
};

export const PRICING_ITEM: NavItem = {
  label: 'Pricing', to: '/pricing',
  icon: <SvgIcon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
};

export const AI_TOOLS_ITEMS: NavItem[] = [
  { label: 'AI Detector',      to: '/ai-tools/detector',       icon: <SvgIcon path="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2v-4M9 21H5a2 2 0 01-2-2v-4m0 0h18" /> },
  { label: 'Humanize',         to: '/ai-tools/humanizer',      icon: <SvgIcon path="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /> },
  { label: 'Journal Finder',   to: '/ai-tools/journal-finder', icon: <SvgIcon path="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" /> },
  { label: 'Journal Browse',   to: '/journals/browse',         icon: <SvgIcon path="M4 6h16M4 10h16M4 14h10M4 18h7" /> },
  { label: 'Run History',      to: '/ai-tools/history',        icon: <SvgIcon path={ICON_HISTORY} /> },
];
